import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  closeCheckout,
  currentCheckout,
  loadStripeJs,
  noCheckout,
  readCheckoutMessage,
  subscribeCheckout,
  type CheckoutOpening,
} from "../../lib/checkout";

export const CHECKOUT_TITLE = "Checkout";
export const CHECKOUT_CLOSE = "Close checkout";
/** What a card form that could not be reached says. Never a stack trace. */
export const CHECKOUT_UNREACHABLE = "The card form did not load. Close this and try again.";

/**
 * The card form, over the screen it was asked for from (ruling R-0825-2).
 *
 * MOUNTED ONCE, AT THE ROUTER'S ROOT, because a checkout outlives the surface
 * that started it in the only way that matters: the billing panel, a door, the
 * wizard and the owner's own thread all open one, and a modal owned by any of
 * them would close when that one re-rendered itself out of existence. What
 * decides whether it is on screen is a module-level value (`lib/checkout.ts`),
 * not a prop and not a route.
 *
 * IT IS A DIALOG, NOT A PAGE. No history entry is pushed, so the back button
 * after a purchase lands where the buyer was before it, not on a spent session.
 *
 * ESCAPE IS HANDLED HERE AND NOWHERE ELSE. The shell's own cascade
 * (`state.tsx`) knows about panels, sheets and the paywall; it cannot know
 * about this, because this is also raised from routes the shell does not
 * contain. It sits above all of them, so it takes the key first — and it takes
 * it in the capture phase for exactly that reason.
 */
export function CheckoutModal() {
  const session = useSyncExternalStore(subscribeCheckout, currentCheckout, noCheckout);

  useEffect(() => {
    if (!session) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      event.preventDefault();
      closeCheckout("unpaid");
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [session]);

  if (!session) return null;

  return (
    <div className="nob-scrim fixed inset-0 z-[60] grid place-items-center p-3">
      {/* Dismissing is a real answer, not an accident to be prevented: it is
          what the processor's cancel URL used to be, and every surface already
          knows how to say "Nothing was charged." */}
      <button
        type="button"
        aria-label={CHECKOUT_CLOSE}
        tabIndex={-1}
        onClick={() => closeCheckout("unpaid")}
        className="absolute inset-0 cursor-default bg-ink/40"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={CHECKOUT_TITLE}
        className="relative flex h-[min(88vh,44rem)] w-[min(30rem,calc(100vw-24px))] flex-col
                   overflow-hidden rounded-xl border border-border bg-bg text-text
                   shadow-[0_8px_40px_rgba(0,0,0,0.18)]"
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border-soft px-4 py-3">
          <h2 className="text-sm font-semibold tracking-tight">{CHECKOUT_TITLE}</h2>
          <button
            type="button"
            onClick={() => closeCheckout("unpaid")}
            aria-label={CHECKOUT_CLOSE}
            className="rounded-full px-2 py-1 text-sm text-muted transition-colors duration-75
                       hover:bg-hover hover:text-text focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            Close
          </button>
        </header>
        <div className="min-h-0 flex-1">
          {session.opening.kind === "page" ? (
            <FramedCheckout opening={session.opening} />
          ) : (
            <EmbeddedCheckoutFrame opening={session.opening} />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * FakeStripe, in a frame.
 *
 * THE SEAM IS A `postMessage`, NOT A URL WATCH. A frame's location is
 * unreadable from the parent the moment the two are on different origins, and
 * against a real api on :3000 they are. So the fake's last page announces the
 * outcome itself and this listens for it — the same shape the real path has,
 * where Stripe.js reports completion upward rather than the parent inspecting
 * anything.
 *
 * The origin is pinned to whatever origin this component put in the `src`.
 * Under the dev proxy that is our own; against the api directly it is the
 * api's. Either way it is one value, computed once, and a message from anywhere
 * else is dropped.
 */
function FramedCheckout({ opening }: { opening: Extract<CheckoutOpening, { kind: "page" }> }) {
  const origin = useRef<string>("");
  if (!origin.current) {
    try {
      origin.current = new URL(opening.url, window.location.origin).origin;
    } catch {
      origin.current = window.location.origin;
    }
  }

  useEffect(() => {
    const expected = origin.current;
    const onMessage = (event: MessageEvent) => {
      const result = readCheckoutMessage(event, expected);
      if (result) closeCheckout(result);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <iframe
      title={CHECKOUT_TITLE}
      src={opening.url}
      className="h-full w-full border-0"
      // The frame is a payment form on our own stack: it needs to submit and to
      // run its own script, and it needs nothing else.
      sandbox="allow-scripts allow-forms allow-same-origin"
    />
  );
}

/**
 * Stripe's own embedded checkout, mounted into this modal.
 *
 * `onComplete` CLOSES A MODAL AND BOOKS NOTHING. The webhook is still the only
 * thing that binds a subscription (`billing/webhook.ts`),
 * exactly as it was when the return trip was a redirect; what changed is where
 * the buyer is standing when it lands, not who is allowed to say the money
 * moved.
 */
function EmbeddedCheckoutFrame({
  opening,
}: {
  opening: Extract<CheckoutOpening, { kind: "embedded" }>;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let mounted: { destroy: () => void } | null = null;

    void loadStripeJs()
      .then(async (Stripe) => {
        const target = host.current;
        if (!live || !target) return;
        const checkout = await Stripe(opening.publishableKey).initEmbeddedCheckout({
          clientSecret: opening.clientSecret,
          onComplete: () => closeCheckout("paid"),
        });
        if (!live) {
          checkout.destroy();
          return;
        }
        mounted = checkout;
        checkout.mount(target);
      })
      .catch(() => {
        if (live) setFailed(true);
      });

    return () => {
      live = false;
      mounted?.destroy();
    };
  }, [opening.clientSecret, opening.publishableKey]);

  return (
    <div className="h-full overflow-y-auto p-4">
      {failed ? (
        <p className="text-sm leading-relaxed text-muted" role="alert">
          {CHECKOUT_UNREACHABLE}
        </p>
      ) : (
        <div ref={host} />
      )}
    </div>
  );
}
